import { describeError, useApi } from '@classgo/app-kit';
import type { AttendanceAlert } from '@classgo/shared';
import { Button, EmptyState, Input, Table, useToast } from '@classgo/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { attendanceApi, attendanceQueries, refusalOf } from '../../attendance/api';
import { useT } from '../../i18n';
import { formatDate } from '../../lib/dates';

/**
 * The school's threshold (#114 over #105): the absence percentage that alerts and the marks a
 * student needs in the period before counting, saved with `attendance:write` and read by anyone
 * with `attendance:read`; under it, the alerts raised so far, newest first.
 */
export const SettingsTab = ({ canWrite }: { canWrite: boolean }) => {
  const t = useT();
  const api = useApi();
  const queries = useQueryClient();
  const toast = useToast();
  const settings = useQuery(attendanceQueries.settings(api));
  const alerts = useQuery(attendanceQueries.alerts(api));
  const [threshold, setThreshold] = useState('');
  const [minimum, setMinimum] = useState('');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!settings.data) return;
    setThreshold(String(settings.data.absenceThresholdPercent));
    setMinimum(String(settings.data.minimumMarks));
  }, [settings.data]);
  const save = useMutation({
    mutationFn: () =>
      attendanceApi(api).saveSettings({
        absenceThresholdPercent: Number(threshold),
        minimumMarks: Number(minimum),
      }),
    onSuccess: (result) => {
      setError(null);
      queries.setQueryData(attendanceQueries.settings(api).queryKey, result);
      toast.show({ tone: 'success', title: t('attendance.settings.saved') });
    },
    onError: (thrown) => setError(refusalOf(thrown) ?? describeError(t, thrown)),
  });

  return (
    <div className="space-y-6">
      <section aria-labelledby="attendance-settings-title" className="space-y-3">
        <h3 id="attendance-settings-title" className="text-base font-semibold text-gray-900">
          {t('attendance.settings.title')}
        </h3>
        <p className="text-sm text-gray-600">{t('attendance.settings.help')}</p>
        {settings.isError ? (
          <EmptyState
            title={t('entity.error.title')}
            description={describeError(t, settings.error)}
          />
        ) : !settings.data ? (
          <p className="text-sm text-gray-500">{t('common.loading')}</p>
        ) : canWrite ? (
          <form
            className="space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              save.mutate();
            }}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <Input
                label={t('attendance.settings.threshold')}
                type="number"
                min={1}
                max={100}
                step={1}
                required
                value={threshold}
                onChange={(event) => setThreshold(event.target.value)}
              />
              <Input
                label={t('attendance.settings.minimumMarks')}
                type="number"
                min={1}
                max={500}
                step={1}
                required
                value={minimum}
                onChange={(event) => setMinimum(event.target.value)}
              />
            </div>
            {settings.data.isDefault ? (
              <p className="text-sm text-gray-600">{t('attendance.settings.default')}</p>
            ) : null}
            {error ? (
              <p
                role="alert"
                className="rounded-control bg-danger-100 px-3 py-2 text-sm text-danger-700"
              >
                {error}
              </p>
            ) : null}
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <Button type="submit" loading={save.isPending}>
                {t('attendance.settings.save')}
              </Button>
            </div>
          </form>
        ) : (
          <p className="text-sm text-gray-900">
            {t('attendance.settings.current', {
              percent: settings.data.absenceThresholdPercent,
              marks: settings.data.minimumMarks,
            })}
            {settings.data.isDefault ? ` ${t('attendance.settings.default')}` : ''}
          </p>
        )}
      </section>
      <section className="space-y-3">
        <h3 className="text-base font-semibold text-gray-900">{t('attendance.alerts.title')}</h3>
        {alerts.isError ? (
          <EmptyState
            title={t('entity.error.title')}
            description={describeError(t, alerts.error)}
          />
        ) : (
          <Table<AttendanceAlert>
            caption={t('attendance.alerts.title')}
            rows={alerts.data?.items ?? []}
            rowKey={(alert) => alert.id}
            loading={alerts.isPending}
            empty={<EmptyState title={t('attendance.alerts.empty')} />}
            columns={[
              {
                key: 'student',
                header: t('attendance.alerts.column.student'),
                cell: (alert) => (
                  <span className="font-medium text-gray-900">{alert.studentName ?? '—'}</span>
                ),
              },
              {
                key: 'group',
                header: t('attendance.alerts.column.group'),
                cell: (alert) => alert.groupName ?? '—',
              },
              {
                key: 'period',
                header: t('attendance.alerts.column.period'),
                cell: (alert) => alert.periodName,
              },
              {
                key: 'absence',
                header: t('attendance.alerts.column.absence'),
                cell: (alert) =>
                  t('attendance.alerts.absence', {
                    percent: alert.absencePercent,
                    threshold: alert.thresholdPercent,
                  }),
              },
              {
                key: 'marks',
                header: t('attendance.alerts.column.marks'),
                cell: (alert) =>
                  t('attendance.alerts.marks', { absences: alert.absences, marks: alert.marks }),
              },
              {
                key: 'date',
                header: t('attendance.alerts.column.date'),
                cell: (alert) => formatDate(alert.createdAt),
              },
            ]}
          />
        )}
      </section>
    </div>
  );
};
